import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import Swiper from 'swiper';

@Injectable({
  providedIn: 'root'
})
export class ShiftScrollService  {
  private _isShiftPressed = new BehaviorSubject<boolean>(false);
  private _keyDownHandler = this._onKeyDown.bind(this);
  private _keyUpHandler = this._onKeyUp.bind(this);
  private _blurHandler = this._onWindowBlur.bind(this);
  private _activeElements = new Set<HTMLElement>();
  private _listenersActive = false;
  private _elementCounter = 0;
  private _elementRegistry = new Map<string, {
    element: HTMLElement;
    subscription: Subscription;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  }>();

  /**
   * Observable que emite true/false cuando se presiona/libera Shift
   */
  get isShiftPressed$(): Observable<boolean> {
    return this._isShiftPressed.asObservable();
  }

  /**
   * Valor actual del estado de Shift (síncrono)
   */
  get isShiftPressed(): boolean {
    return this._isShiftPressed.value;
  }

  /**
   * Inicializar los event listeners globales (solo cuando se necesitan)
   */
  private _initializeListeners(): void {
    if (this._listenersActive) return;

    document.addEventListener('keydown', this._keyDownHandler);
    document.addEventListener('keyup', this._keyUpHandler);
    window.addEventListener('blur', this._blurHandler);
    
    this._listenersActive = true;
    // console.log('ShiftScrollService listeners activados');
  }

  /**
   * Remover los event listeners globales (cuando no se necesitan)
   */
  private _removeListeners(): void {
    if (!this._listenersActive) return;

    document.removeEventListener('keydown', this._keyDownHandler);
    document.removeEventListener('keyup', this._keyUpHandler);
    window.removeEventListener('blur', this._blurHandler);
    
    this._listenersActive = false;
    this._isShiftPressed.next(false); // Reset state
    // console.log('ShiftScrollService listeners desactivados');
  }

  private _onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Shift' && !this._isShiftPressed.value) {
      this._isShiftPressed.next(true);
    }
  }

  private _onKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Shift' && this._isShiftPressed.value) {
      this._isShiftPressed.next(false);
      // console.log('Shift liberado - Scroll vertical normal');
    }
  }

  private _onWindowBlur(): void {
    // Resetear estado si se pierde el foco (por seguridad)
    if (this._isShiftPressed.value) {
      this._isShiftPressed.next(false);
      // console.log('Foco perdido - Reseteando estado de Shift');
    }
  }

  /**
   * Configurar un elemento para que responda al Shift + scroll
   * @param swiperInstance Instancia de Swiper
   * @param element Elemento HTML del swiper (opcional)
   * @returns ID único del elemento registrado
   */
  setupSwiperShiftScroll(swiperInstance: Swiper, element?: HTMLElement): string {
    const elementId = this._generateElementId();
    const targetElement = element || swiperInstance.el;
    
    if (!targetElement) {
      console.warn('No se encontró elemento para ShiftScrollService');
      return elementId;
    }

    // Configurar listeners de mouse y subscription
    this._setupElementListeners(elementId, targetElement, swiperInstance);
    
    // console.log(`ShiftScrollService configurado para elemento ${elementId}`);
    return elementId;
  }

  /**
   * Limpiar un elemento específico del servicio
   * @param elementId ID del elemento a limpiar
   */
  cleanupElement(elementId: string): void {
    const elementData = this._elementRegistry.get(elementId);
    
    if (!elementData) {
      console.warn(`Elemento ${elementId} no encontrado para limpieza`);
      return;
    }

    // Remover listeners de mouse
    elementData.element.removeEventListener('mouseenter', elementData.onMouseEnter);
    elementData.element.removeEventListener('mouseleave', elementData.onMouseLeave);
    
    // Remover elemento activo
    this._activeElements.delete(elementData.element);
    
    // Si no hay más elementos activos, remover listeners globales
    if (this._activeElements.size === 0) {
      this._removeListeners();
    }
    
    // Limpiar subscription
    elementData.subscription.unsubscribe();
    
    // Remover del registro
    this._elementRegistry.delete(elementId);

    // console.log(`Elemento ${elementId} removido del ShiftScrollService`);
  }

  /**
   * Generar un ID único para cada elemento registrado
   */
  private _generateElementId(): string {
    return `shift-scroll-${++this._elementCounter}`;
  }

  /**
   * Configurar listeners de mouse y subscription para un elemento específico
   */
  private _setupElementListeners(elementId: string, element: HTMLElement, swiperInstance: Swiper): void {
    // Handlers para mouse enter/leave
    const onMouseEnter = () => {
      this._activeElements.add(element);
      this._initializeListeners();
      // console.log(`Mouse sobre elemento ${elementId} - Shift+scroll habilitado`);
    };

    const onMouseLeave = () => {
      this._activeElements.delete(element);
      if (this._activeElements.size === 0) {
        this._removeListeners();
      }
      // console.log(`Mouse fuera de elemento ${elementId} - Shift+scroll deshabilitado`);
    };

    // Subscription para controlar el mousewheel del swiper
    const subscription = this.isShiftPressed$.subscribe(isPressed => {
      if (swiperInstance && !swiperInstance.destroyed) {
        if (isPressed) {
          swiperInstance.mousewheel.enable();
          element.style.cursor = 'grab';
        } else {
          swiperInstance.mousewheel.disable();
          element.style.cursor = 'default';
        }
      }
    });

    // Agregar listeners de mouse
    element.addEventListener('mouseenter', onMouseEnter);
    element.addEventListener('mouseleave', onMouseLeave);

    // Registrar elemento con toda su información
    this._elementRegistry.set(elementId, {
      element,
      subscription,
      onMouseEnter,
      onMouseLeave
    });
  }
}
